import { Link } from 'react-router-dom';
import { CalendarDays, Mail, MapPin } from 'lucide-react';
import { Markdown } from '@/lib/markdown';
import { dateTime, money } from '@/lib/format';
import type { EventOption } from '@/lib/queries';

export interface EventView {
  id?: string; title: string; type: string; description: string | null; body_md: string | null; starts_at: string; ends_at: string | null;
  venue: string | null; venue_address: string | null; contact_email: string | null; registration_closes_at: string | null;
  requires_active_membership: boolean; grading_offered?: boolean; options: EventOption[];
}

const TYPE_LABEL: Record<string, string> = { agm: 'AGM', gasshuku: 'Gasshuku', grading: 'Grading', course: 'Course', competition: 'Competition', seminar: 'Seminar', other: 'Event' };

/** The public event page body. Also used as the live preview in the admin editor, so admins see exactly what members will. */
export function EventDetails({ e, bookHref, gradingHref, preview = false }: { e: EventView; bookHref?: string; gradingHref?: string; preview?: boolean }) {
  const options = e.options.filter(o => o.active !== false && o.name).sort((a, b) => a.sort_order - b.sort_order);
  const closed = (e.registration_closes_at && e.registration_closes_at < new Date().toISOString()) || e.starts_at < new Date().toISOString();
  const map = e.venue_address ? `https://www.google.com/maps/search/?api=1&query=${encodeURIComponent(e.venue_address)}` : null;
  return (
    <article className="space-y-6">
      <header className="relative overflow-hidden border border-stone-200 bg-white px-6 py-10 sm:px-10">
        <span aria-hidden="true" className="pointer-events-none absolute -right-4 -top-8 select-none font-jp text-[180px] font-extrabold leading-none text-ink-900 opacity-[0.035]">剛柔</span>
        <p className="eyebrow relative">{TYPE_LABEL[e.type] ?? e.type}</p>
        <h1 className="relative mt-2 text-4xl leading-tight sm:text-5xl">{e.title || 'Untitled event'}</h1>
        {e.description && <p className="relative mt-4 max-w-2xl text-lg text-ink-700">{e.description}</p>}
        <div className="relative mt-5 flex flex-wrap gap-x-6 gap-y-2 text-[15px] text-ink-500">
          {e.starts_at && <span className="flex items-center gap-1.5"><CalendarDays size={16} />{dateTime(e.starts_at)}{e.ends_at && ` to ${dateTime(e.ends_at)}`}</span>}
          {e.venue && <span className="flex items-center gap-1.5"><MapPin size={16} />{e.venue}</span>}
        </div>
      </header>

      <div className="grid gap-6 lg:grid-cols-[1fr_320px]">
        <div className="card">{e.body_md?.trim() ? <Markdown source={e.body_md} /> : <p className="text-sm text-ink-500">{preview ? 'The details you write will appear here.' : 'More details coming soon.'}</p>}</div>

        <aside className="space-y-4 lg:sticky lg:top-4 lg:self-start">
          <div className="space-y-3 border border-ink-900 bg-white p-5">
            <h2 className="text-xl">Prices</h2>
            {options.length ? (
              <ul className="space-y-1.5 text-sm">{options.map(o => <li key={o.id ?? o.name} className="flex justify-between gap-3"><span>{o.name}</span><span className="font-semibold">{o.price_pence ? money(o.price_pence) : 'Free'}</span></li>)}</ul>
            ) : <p className="text-sm text-ink-500">Prices to be announced.</p>}
            <p className="text-xs text-ink-500">{e.requires_active_membership ? 'EGKA members only: your membership must cover the event date.' : 'Open to everyone.'}{e.registration_closes_at && ` Booking closes ${dateTime(e.registration_closes_at)}.`}</p>
            {bookHref && !closed && <Link to={bookHref} className="btn-primary w-full">Book</Link>}
            {preview && <span className="btn-primary w-full opacity-60">Book</span>}
            {closed && !preview && <p className="text-sm font-medium text-ink-700">Booking has closed.</p>}
          </div>
          {e.grading_offered && (
            <div className="card space-y-2">
              <h2 className="text-lg">Grading</h2>
              <p className="text-[15px] text-ink-700">There's a grading at this event. Enter online and your sensei will be asked to approve. The fee is paid on the day.</p>
              {gradingHref && !closed && <Link to={gradingHref} className="btn w-full border border-ink-900 text-ink-900 hover:bg-stone-100">Enter the grading</Link>}
            </div>
          )}
          {(e.venue_address || e.contact_email) && (
            <div className="card space-y-3 text-sm">
              {e.venue_address && <div><h2 className="text-lg">Venue</h2><p className="mt-1 whitespace-pre-line">{e.venue_address}</p>{map && <a href={map} target="_blank" rel="noreferrer" className="mt-1 inline-flex items-center gap-1 text-brand-700 underline"><MapPin size={14} />Open in maps</a>}</div>}
              {e.contact_email && <div><h2 className="text-lg">Questions</h2><a href={`mailto:${e.contact_email}`} className="mt-1 inline-flex items-center gap-1 text-brand-700 underline"><Mail size={14} />{e.contact_email}</a></div>}
            </div>
          )}
        </aside>
      </div>
    </article>
  );
}
